// src/pages/admin/SupportManagement.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, IconButton, Typography, useMediaQuery, useTheme,
  Drawer, CircularProgress, Alert, Snackbar, Button, TextField,
  InputAdornment, Chip, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl,
  Divider, InputLabel, Autocomplete, FormHelperText, Checkbox,
  List, ListItem, ListItemButton, Avatar,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Search, Refresh, Close, SupportAgent, Send,
  CheckCircle, Schedule, Lock, Inbox, Person, AdminPanelSettings,
  Phone, Email, WhatsApp, Chat, Add, HeadsetMic,
  ChevronLeft, ChevronRight, ShoppingCart, Inventory2, DeleteOutline,
  Visibility, Reply,
} from '@mui/icons-material';

import Slidemenu   from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListAllTicketsQuery,
  useGetConversationQuery,
  useSendReplyMutation,
  useUpdateTicketStatusMutation,
  useCreateTicketMutation,
  useDeleteTicketMutation,
  useLazyGetUserOrdersQuery,
  useLazyGetOrderProductsQuery,
  type SupportTicket,
  type TicketStatusNum,
  type TicketChannel,
  type CreateTicketPayload,
  type UserOrder,
  type OrderProduct,
  type TicketProduct,
  STATUS_LABEL,
}  from '../../features/admin/supportApi.ts';
import { useListUsersQuery } from '../../features/admin/userlistApi.ts';

// ── Channel config ─────────────────────────────────────────────────────────────

interface ChannelCfg { label: string; icon: React.ReactElement; color: string; bgcolor: string; }

const CHANNEL_CONFIG: Record<TicketChannel, ChannelCfg> = {
  help_resolution: { label: 'Help & Resolution', icon: <HeadsetMic sx={{ fontSize: 13 }} />, color: '#0a58ca', bgcolor: '#cfe2ff' },
  email:           { label: 'Email',             icon: <Email      sx={{ fontSize: 13 }} />, color: '#6d4c41', bgcolor: '#efebe9' },
  whatsapp:        { label: 'WhatsApp',          icon: <WhatsApp   sx={{ fontSize: 13 }} />, color: '#1b5e20', bgcolor: '#c8e6c9' },
  tollfree:        { label: 'Toll-Free',         icon: <Phone      sx={{ fontSize: 13 }} />, color: '#6a1b9a', bgcolor: '#f3e5f5' },
  livechat:        { label: 'Live Chat',         icon: <Chat       sx={{ fontSize: 13 }} />, color: '#e65100', bgcolor: '#fff3e0' },
};
const CHANNEL_KEYS = Object.keys(CHANNEL_CONFIG) as TicketChannel[];

const NATURE_OPTIONS = [
  'Product Defect', 'Wrong Item Delivered', 'Delivery Delay', 'Payment Issue',
  'Refund Request', 'Order Cancellation', 'Exchange Request',
  'App/Website Issue', 'Account Problem', 'Other',
];

// ── Status config ──────────────────────────────────────────────────────────────

interface StatusCfg { bgcolor: string; color: string; icon: React.ReactElement; }

const STATUS_CONFIG: Record<TicketStatusNum, StatusCfg> = {
  0: { bgcolor: '#fff3cd', color: '#856404', icon: <Inbox       sx={{ fontSize: 13 }} /> },
  1: { bgcolor: '#cfe2ff', color: '#0a58ca', icon: <Schedule    sx={{ fontSize: 13 }} /> },
  2: { bgcolor: '#d1f4e0', color: '#00783e', icon: <CheckCircle sx={{ fontSize: 13 }} /> },
  3: { bgcolor: '#e4e5e7', color: '#616161', icon: <Lock        sx={{ fontSize: 13 }} /> },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtDateTime = (d?: string) =>
  d ? new Date(d).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

interface UserOption { id: number; full_name: string; email: string; phone: string; }

const emptyForm = (): CreateTicketPayload => ({
  channel: 'help_resolution', name: '', email: '', phone: '',
  order_id: '', product_ids: [], subject: '', nature_of_complaint: '', description: '', status: 0,
});

type AnyTicket = SupportTicket & Record<string, unknown>;
const ticketChannel = (t: SupportTicket): TicketChannel => {
  const ch = (t as AnyTicket).channel as string | undefined;
  return ch && CHANNEL_KEYS.includes(ch as TicketChannel) ? ch as TicketChannel : 'help_resolution';
};
const ticketNature  = (t: SupportTicket) => String((t as AnyTicket).nature_of_complaint ?? '').trim() || t.subject || '';
const ticketByAdmin = (t: SupportTicket) => Boolean((t as AnyTicket).raised_by_admin);
const getChannelCfg = (ch: TicketChannel) => CHANNEL_CONFIG[ch];

// ── Order status short label ───────────────────────────────────────────────────
const ORDER_STATUS: Record<number, string> = {
  0:'Pending', 1:'Confirmed', 2:'Processing', 3:'Pickup Pending', 4:'Warehouse Pickup',
  5:'In Transit', 6:'Out for Delivery', 7:'Delivered', 8:'Not Delivered',
  9:'Cancelled', 12:'Cancelled by Admin',
};
const orderStatusLabel = (s: number) => ORDER_STATUS[s] ?? `Status ${s}`;

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const SupportManagement: React.FC = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── List / filter state ────────────────────────────────────────────────────
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [statusTab,      setStatusTab]      = useState(0);
  const [channelFilter,  setChannelFilter]  = useState<TicketChannel | 'all'>('all');
  const [searchTerm,     setSearchTerm]     = useState('');

  // ── View Ticket Dialog state ────────────────────────────────────────────────
  const [viewTicket, setViewTicket] = useState<SupportTicket | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);

  // ── Reply Dialog state ──────────────────────────────────────────────────────
  const [replyTicket, setReplyTicket] = useState<SupportTicket | null>(null);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState<TicketStatusNum>(1);

  // ── Create ticket dialog state ─────────────────────────────────────────────
  const [openNewTicket,    setOpenNewTicket]    = useState(false);
  const [form,             setForm]             = useState<CreateTicketPayload>(emptyForm());
  const [selectedUser,     setSelectedUser]     = useState<UserOption | null>(null);
  const [selectedOrder,    setSelectedOrder]    = useState<UserOrder | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  // ── Delete confirmation ────────────────────────────────────────────────────
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<number | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const bottomRef      = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ── RTK Query ──────────────────────────────────────────────────────────────
  const { data, isLoading, isError, refetch } = useListAllTicketsQuery();
  const { data: usersData, isLoading: usersLoading } = useListUsersQuery();
  
  // Separate queries for view and reply
  const { data: viewConvData, isFetching: loadingViewChat, refetch: refetchViewConv } =
    useGetConversationQuery(viewTicket?.id ?? 0, { skip: !viewTicket });
    
  const { data: replyConvData, isFetching: loadingReplyChat, refetch: refetchReplyConv } =
    useGetConversationQuery(replyTicket?.id ?? 0, { skip: !replyTicket });

  const [fetchUserOrders,   { data: ordersData,   isFetching: fetchingOrders }]   = useLazyGetUserOrdersQuery();
  const [fetchOrderProducts,{ data: productsData, isFetching: fetchingProducts }] = useLazyGetOrderProductsQuery();

  const [sendReply,    { isLoading: sendingReply }]   = useSendReplyMutation();
  const [updateStatus, { isLoading: updatingStatus }] = useUpdateTicketStatusMutation();
  const [createTicket, { isLoading: creatingTicket }] = useCreateTicketMutation();
  const [deleteTicket, { isLoading: deletingTicket }] = useDeleteTicketMutation();

  const tickets: SupportTicket[] = data?.tickets ?? [];
  const viewReplies = viewConvData?.replies ?? [];
  const viewTicketProducts = viewConvData?.ticket?.products ?? [];
  const replyReplies = replyConvData?.replies ?? [];
  const replyTicketProducts = replyConvData?.ticket?.products ?? [];
  const userOrders: UserOrder[]  = ordersData?.orders ?? [];
  const orderProducts: OrderProduct[] = productsData?.products ?? [];

  const userOptions: UserOption[] = React.useMemo(() => {
    const list: any[] = usersData?.data ?? usersData?.users ?? [];
    return list.map((u) => ({
      id:        u.user_id ?? u.id,
      full_name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.full_name || '',
      email:     u.user_email ?? u.email ?? '',
      phone:     u.user_contact ?? u.phone ?? u.contact ?? '',
    }));
  }, [usersData]);

  // ── Scroll sync ────────────────────────────────────────────────────────────
  const syncScrollState = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const setTableRef = useCallback((node: HTMLDivElement | null) => {
    if (tableScrollRef.current) tableScrollRef.current.removeEventListener('scroll', syncScrollState);
    tableScrollRef.current = node;
    if (!node) return;
    node.addEventListener('scroll', syncScrollState);
    window.addEventListener('resize', syncScrollState);
    setTimeout(syncScrollState, 0);
  }, [syncScrollState]);

  useEffect(() => {
    if (!openReplyDialog) return;
    const id = setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    return () => clearTimeout(id);
  }, [replyReplies, openReplyDialog]);

  // ── User select → fetch orders ─────────────────────────────────────────────
  const handleUserSelect = (user: UserOption | null) => {
    setSelectedUser(user);
    setSelectedOrder(null);
    setSelectedProducts([]);
    setForm(prev => ({ ...prev, name: user?.full_name ?? '', email: user?.email ?? '', phone: user?.phone ?? '', order_id: '', product_ids: [] }));

    if (user) fetchUserOrders(user.id);
  };

  // ── Order select → fetch products ─────────────────────────────────────────
  const handleOrderSelect = (orderId: string) => {
    const order = userOrders.find(o => String(o.order_id) === orderId) ?? null;
    setSelectedOrder(order);
    setSelectedProducts([]);
    setForm(prev => ({ ...prev, order_id: orderId, product_ids: [] }));

    // fetchOrderProducts expects number
    if (orderId && !isNaN(Number(orderId))) {
      fetchOrderProducts(Number(orderId));
    }
  };

  // ── Product toggle ─────────────────────────────────────────────────────────
  const toggleProduct = (productId: number) => {
    setSelectedProducts(prev => {
      const next = prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId];
      setForm(f => ({ ...f, product_ids: next }));
      return next;
    });
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const TAB_STATUS: (TicketStatusNum | -1)[] = [-1, 0, 1, 2, 3];
  const TAB_LABELS = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];

  const filtered = tickets.filter(t => {
    const term = searchTerm.toLowerCase();
    const matchSearch =
      t.subject.toLowerCase().includes(term) ||
      (t.invoice_no ?? '').toLowerCase().includes(term) ||
      t.display_name.toLowerCase().includes(term) ||
      t.email.toLowerCase().includes(term) ||
      t.phone.toLowerCase().includes(term) ||
      ticketNature(t).toLowerCase().includes(term);
    const matchStatus  = statusTab === 0 || t.status === TAB_STATUS[statusTab];
    const matchChannel = channelFilter === 'all' || ticketChannel(t) === channelFilter;
    return matchSearch && matchStatus && matchChannel;
  });

  const countByTab     = (i: number) => tickets.filter(t => (i === 0 || t.status === TAB_STATUS[i]) && (channelFilter === 'all' || ticketChannel(t) === channelFilter)).length;
  const countByChannel = (ch: TicketChannel | 'all') => ch === 'all' ? tickets.length : tickets.filter(t => ticketChannel(t) === ch).length;

  const showSnack = (message: string, severity: 'success' | 'error') => setSnackbar({ open: true, message, severity });

  // ── View Dialog handlers ────────────────────────────────────────────────────
  const handleViewTicket = (t: SupportTicket, event: React.MouseEvent) => {
    event.stopPropagation();
    setViewTicket(t);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewTicket(null);
  };

  // ── Reply Dialog handlers ───────────────────────────────────────────────────
  const handleReplyTicket = (t: SupportTicket, event: React.MouseEvent) => {
    event.stopPropagation();
    setReplyTicket(t);
    setReplyStatus(t.status === 0 ? 1 : t.status);
    setReplyText('');
    setOpenReplyDialog(true);
  };

  const handleCloseReplyDialog = () => {
    setOpenReplyDialog(false);
    setReplyTicket(null);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !replyTicket) return;
    try {
      await sendReply({ ticket_id: replyTicket.id, message: replyText.trim(), status: replyStatus }).unwrap();
      setReplyText('');
      refetchReplyConv();
      refetch();
      showSnack('Reply sent!', 'success');
    } catch {
      showSnack('Failed to send reply', 'error');
    }
  };

  const handleQuickStatus = async (ticketId: number, status: TicketStatusNum, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await updateStatus({ ticket_id: ticketId, status }).unwrap();
      showSnack(`Status updated to "${STATUS_LABEL[status]}"`, 'success');
      refetch();
    } catch {
      showSnack('Failed to update status', 'error');
    }
  };

  // ── Delete handler ─────────────────────────────────────────────────────────
  const handleDeleteTicket = async (ticketId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setTicketToDelete(ticketId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!ticketToDelete) return;
    try {
      await deleteTicket(ticketToDelete).unwrap();
      showSnack('Ticket deleted successfully', 'success');
      refetch();
      if (viewTicket?.id === ticketToDelete) handleCloseViewDialog();
      if (replyTicket?.id === ticketToDelete) handleCloseReplyDialog();
    } catch {
      showSnack('Failed to delete ticket', 'error');
    } finally {
      setConfirmDeleteOpen(false);
      setTicketToDelete(null);
    }
  };

  // ── Create ticket ──────────────────────────────────────────────────────────
  const handleCreateTicket = async () => {
    // Convert order_id to string for validation
    const orderIdStr = String(form.order_id || '');
    
    if (!form.name.trim())            { showSnack('Please select a customer', 'error');                return; }
    if (!orderIdStr.trim())           { showSnack('Please select an order', 'error');                  return; }
    if (form.product_ids.length === 0){ showSnack('Please select at least one product', 'error');      return; }
    if (!form.nature_of_complaint)    { showSnack('Nature of Complaint is required', 'error');         return; }
    if (!form.subject.trim())         { showSnack('Subject is required', 'error');                     return; }
    
    try {
      // Create a payload with order_id as string
      const payload = {
        ...form,
        order_id: orderIdStr,
      };
      await createTicket(payload).unwrap();
      showSnack('Ticket created successfully!', 'success');
      setOpenNewTicket(false);
      resetForm();
      refetch();
    } catch (error: any) {
      showSnack(error?.data?.message || 'Failed to create ticket', 'error');
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    setSelectedUser(null);
    setSelectedOrder(null);
    setSelectedProducts([]);
  };

  const closeNew = () => { setOpenNewTicket(false); resetForm(); };

  // ── Shared sx ──────────────────────────────────────────────────────────────
  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px' } };
  const tabSx = {
    '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '13px', minHeight: 44, color: '#6d7175', px: 1.5 },
    '& .Mui-selected':      { color: '#202223', fontWeight: 600 },
    '& .MuiTabs-indicator': { backgroundColor: '#008060', height: 3 },
  };
  const chipSx = (active: boolean, cfg: { color: string; bgcolor: string }) => ({
    cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: '12px', height: 28,
    bgcolor: active ? cfg.bgcolor : '#f1f2f4', color: active ? cfg.color : '#6d7175',
    border: active ? `1.5px solid ${cfg.color}` : '1.5px solid transparent',
    '&:hover': { bgcolor: cfg.bgcolor, opacity: 0.85 },
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(v => !v)} />
        </Box>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
          <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={() => setMobileOpen(v => !v)} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* Page header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Support Management</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" startIcon={<Add />} onClick={() => setOpenNewTicket(true)}
                    sx={{ textTransform: 'none', bgcolor: '#008060', borderRadius: '8px', fontWeight: 500, fontSize: '13px', '&:hover': { bgcolor: '#006e52' } }}>
                    Create Ticket
                  </Button>
                  <Button variant="outlined" startIcon={<Refresh />} onClick={() => refetch()}
                    sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                    Refresh
                  </Button>
                </Box>
              </Box>

              {/* Stat cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 2, mb: 3 }}>
                {([0, 1, 2, 3] as TicketStatusNum[]).map(s => {
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <Card key={s} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e1e3e5', boxShadow: 'none', textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: cfg.color }}>{tickets.filter(t => t.status === s).length}</Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 500 }}>{STATUS_LABEL[s]}</Typography>
                    </Card>
                  );
                })}
              </Box>

              {/* Table card */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>

                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs value={statusTab} onChange={(_, v: number) => { setStatusTab(v); setSearchTerm(''); }}
                    sx={{ px: 2, ...tabSx }} variant="scrollable" scrollButtons="auto">
                    {TAB_LABELS.map((label, i) => <Tab key={label} label={`${label} (${countByTab(i)})`} />)}
                  </Tabs>
                </Box>

                <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: 'white' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, mr: 0.5 }}>Channel:</Typography>
                    <Chip label={`All (${countByChannel('all')})`} size="small" onClick={() => setChannelFilter('all')}
                      sx={chipSx(channelFilter === 'all', { color: '#00783e', bgcolor: '#e8f5e9' })} />
                    {CHANNEL_KEYS.map(ch => {
                      const cfg = CHANNEL_CONFIG[ch];
                      return <Chip key={ch} label={`${cfg.label} (${countByChannel(ch)})`} size="small" icon={cfg.icon}
                        onClick={() => setChannelFilter(ch)} sx={chipSx(channelFilter === ch, cfg)} />;
                    })}
                  </Box>
                  <TextField size="small" placeholder="Search by subject, name, email, phone or complaint type…"
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    sx={{ width: '100%', maxWidth: 560, ...fieldSx }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                </Box>

                {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}><CircularProgress size={32} sx={{ color: '#008060' }} /></Box>}
                {isError && !isLoading && <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load tickets. <Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button></Alert></Box>}
                {!isLoading && !isError && filtered.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <SupportAgent sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>No tickets found</Typography>
                  </Box>
                )}

                {/* Desktop table */}
                {!isLoading && !isError && filtered.length > 0 && !isMobile && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e0e0e0', px: 2, pt: 1 }}>
                      <IconButton onClick={() => tableScrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' })} size="small" disabled={!canScrollLeft}
                        sx={{ flexShrink: 0, width: 28, height: 28, borderRadius: 0, bgcolor: '#f5f5f5', border: '1px solid #c0c0c0', borderRight: 'none', color: canScrollLeft ? '#444' : '#bbb', '&:hover:not(:disabled)': { bgcolor: '#e0e0e0' } }}>
                        <ChevronLeft sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Box sx={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', height: 14, '&::-webkit-scrollbar': { height: 14 }, '&::-webkit-scrollbar-track': { bgcolor: '#f5f5f5' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#aaa', borderRadius: 2, border: '2px solid #f5f5f5' } }}>
                        <Box sx={{ height: 1, width: '100%' }} />
                      </Box>
                      <IconButton onClick={() => tableScrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' })} size="small" disabled={!canScrollRight}
                        sx={{ flexShrink: 0, width: 28, height: 28, borderRadius: 0, bgcolor: '#f5f5f5', border: '1px solid #c0c0c0', borderLeft: 'none', color: canScrollRight ? '#444' : '#bbb', '&:hover:not(:disabled)': { bgcolor: '#e0e0e0' } }}>
                        <ChevronRight sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    <TableContainer ref={setTableRef} sx={{ overflowX: 'auto', bgcolor: 'white' }}>
                      <Table sx={{ minWidth: 1200 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafbfb' }}>
                            {['Ticket ID','Date & Time','Customer','Email','Phone','Channel','Nature of Complaint','Order ID','Status','Actions'].map(h => (
                              <TableCell key={h} sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175', whiteSpace: 'nowrap', ...(h === 'Actions' ? { textAlign: 'right' } : {}) }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filtered.map(ticket => {
                            const sCfg = STATUS_CONFIG[ticket.status];
                            const chCfg = getChannelCfg(ticketChannel(ticket));
                            return (
                              <TableRow key={ticket.id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ color: '#008060', fontWeight: 700, fontSize: '13px' }}>#{ticket.id}</Typography>
                                    {ticketByAdmin(ticket) && <Tooltip title="Created by Admin"><AdminPanelSettings sx={{ fontSize: 14, color: '#00783e' }} /></Tooltip>}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="body2" sx={{ fontSize: '12px' }}>{fmtDateTime(ticket.created_at)}</Typography></TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>{ticket.display_name}</Typography>
                                  {ticket.is_guest && <Chip label="Guest" size="small" sx={{ fontSize: '9px', height: 15, mt: 0.4, bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 }} />}
                                </TableCell>
                                <TableCell>
                                  {ticket.email && ticket.email !== '-'
                                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Email sx={{ fontSize: 12, color: '#6d7175' }} /><Typography variant="body2" sx={{ fontSize: '12px', color: '#1565c0' }}>{ticket.email}</Typography></Box>
                                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                                </TableCell>
                                <TableCell>
                                  {ticket.phone && ticket.phone !== '-'
                                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Phone sx={{ fontSize: 12, color: '#6d7175' }} /><Typography variant="body2" sx={{ fontSize: '12px' }}>{ticket.phone}</Typography></Box>
                                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                                </TableCell>
                                <TableCell>
                                  <Chip label={chCfg.label} size="small" icon={chCfg.icon} sx={{ fontSize: '11px', fontWeight: 600, bgcolor: chCfg.bgcolor, color: chCfg.color, height: 22 }} />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 180 }}>
                                  <Typography variant="body2" sx={{ fontSize: '12px' }}>{ticketNature(ticket) || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                  {ticket.order_id
                                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ShoppingCart sx={{ fontSize: 13, color: '#6d7175' }} /><Typography variant="body2" sx={{ fontSize: '12px' }}>#{ticket.order_id}</Typography></Box>
                                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                                </TableCell>
                                <TableCell>
                                  <Chip label={STATUS_LABEL[ticket.status]} size="small" icon={sCfg.icon} sx={{ fontSize: '11px', fontWeight: 600, bgcolor: sCfg.bgcolor, color: sCfg.color }} />
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                    <Tooltip title="View Details">
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => handleViewTicket(ticket, e)}
                                        sx={{ color: '#1565c0', '&:hover': { bgcolor: '#e3f2fd' } }}>
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Reply">
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => handleReplyTicket(ticket, e)}
                                        sx={{ color: '#008060', '&:hover': { bgcolor: '#e8f5e9' } }}>
                                        <Reply fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {ticket.status !== 2 && (
                                      <Tooltip title="Mark Resolved">
                                        <IconButton size="small" onClick={(e) => handleQuickStatus(ticket.id, 2, e)} sx={{ color: '#00783e', '&:hover': { bgcolor: '#d1f4e0' } }}>
                                          <CheckCircle fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {ticket.status !== 3 && (
                                      <Tooltip title="Close Ticket">
                                        <IconButton size="small" onClick={(e) => handleQuickStatus(ticket.id, 3, e)} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                          <Lock fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Delete Ticket">
                                      <IconButton 
                                        size="small" 
                                        onClick={(e) => handleDeleteTicket(ticket.id, e)}
                                        sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#fdecea' } }}>
                                        <DeleteOutline fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Mobile cards */}
                {!isLoading && !isError && filtered.length > 0 && isMobile && (
                  <Box sx={{ p: 2, bgcolor: 'white' }}>
                    {filtered.map(ticket => {
                      const sCfg = STATUS_CONFIG[ticket.status];
                      const chCfg = getChannelCfg(ticketChannel(ticket));
                      return (
                        <Card key={ticket.id} sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                                <Typography variant="caption" sx={{ color: '#008060', fontWeight: 700 }}>#{ticket.id}</Typography>
                                <Chip label={chCfg.label} size="small" sx={{ fontSize: '9px', height: 16, bgcolor: chCfg.bgcolor, color: chCfg.color, fontWeight: 600 }} />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{ticket.subject}</Typography>
                              <Typography variant="caption" sx={{ color: '#6d7175' }}>{ticket.display_name}</Typography>
                            </Box>
                            <Chip label={STATUS_LABEL[ticket.status]} size="small" sx={{ fontSize: '10px', fontWeight: 600, bgcolor: sCfg.bgcolor, color: sCfg.color, alignSelf: 'flex-start' }} />
                          </Box>
                          {ticket.order_id && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}><ShoppingCart sx={{ fontSize: 14, color: '#6d7175' }} /><Typography variant="body2" sx={{ fontSize: '12px' }}>Order #{ticket.order_id}</Typography></Box>}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">{fmtDateTime(ticket.created_at)}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={(e) => handleViewTicket(ticket, e)} sx={{ color: '#1565c0' }}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reply">
                                <IconButton size="small" onClick={(e) => handleReplyTicket(ticket, e)} sx={{ color: '#008060' }}>
                                  <Reply fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Ticket">
                                <IconButton size="small" onClick={(e) => handleDeleteTicket(ticket.id, e)} sx={{ color: '#d32f2f' }}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/*  VIEW TICKET DIALOG - Shows all details without conversation          */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: '85vh' } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e1e3e5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#202223' }}>
                Ticket #{viewTicket?.id} - {viewTicket?.subject}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6d7175' }}>View Ticket Details</Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseViewDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
          {viewTicket && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Ticket Header Info */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, bgcolor: '#f8f9fa', borderRadius: '12px' }}>
                <Chip label={`Status: ${STATUS_LABEL[viewTicket.status]}`} 
                  sx={{ bgcolor: STATUS_CONFIG[viewTicket.status].bgcolor, color: STATUS_CONFIG[viewTicket.status].color, fontWeight: 600 }} />
                <Chip label={`Channel: ${getChannelCfg(ticketChannel(viewTicket)).label}`} 
                  icon={getChannelCfg(ticketChannel(viewTicket)).icon}
                  sx={{ bgcolor: getChannelCfg(ticketChannel(viewTicket)).bgcolor, color: getChannelCfg(ticketChannel(viewTicket)).color }} />
                <Chip label={`Created: ${fmtDateTime(viewTicket.created_at)}`} variant="outlined" />
                {viewTicket.raised_by_admin && <Chip label="Created by Admin" icon={<AdminPanelSettings />} color="primary" variant="outlined" />}
              </Box>

              {/* Customer Information */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1 }}>Customer Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: '12px' }}>
                <Box><Typography variant="caption" color="text.secondary">Name</Typography><Typography variant="body2">{viewTicket.display_name}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2">{viewTicket.email}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2">{viewTicket.phone}</Typography></Box>
                {viewTicket.is_guest && <Box><Typography variant="caption" color="text.secondary">Type</Typography><Chip label="Guest" size="small" sx={{ bgcolor: '#fff3cd', color: '#856404' }} /></Box>}
              </Box>

              {/* Order Information */}
              {viewTicket.order_id && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1 }}>Order Information</Typography>
                  <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ShoppingCart sx={{ fontSize: 18, color: '#008060' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Order #{viewTicket.order_id}</Typography>
                      {viewTicket.invoice_no && <Typography variant="caption" color="text.secondary">({viewTicket.invoice_no})</Typography>}
                    </Box>
                  </Box>
                </>
              )}

              {/* Products Information */}
              {viewTicketProducts && viewTicketProducts.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1 }}>Selected Products</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {viewTicketProducts.map((product: TicketProduct) => (
                      <Box key={product.product_id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: '12px' }}>
                        {product.image_url ? (
                          <Avatar src={product.image_url} variant="rounded" sx={{ width: 50, height: 50 }} />
                        ) : (
                          <Box sx={{ width: 50, height: 50, bgcolor: '#e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Inventory2 />
                          </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.product_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Quantity: {product.quantity} | Price: ₹{Number(product.unit_price).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {/* Ticket Details */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1 }}>Ticket Details</Typography>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '12px' }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Nature of Complaint</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{viewTicket.nature_of_complaint || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewTicket.description || '—'}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e1e3e5' }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">Close</Button>
          {viewTicket && (
            <Button 
              onClick={(e) => {
                handleCloseViewDialog();
                handleReplyTicket(viewTicket, e as any);
              }} 
              variant="contained" 
              startIcon={<Reply />}
              sx={{ bgcolor: '#008060', '&:hover': { bgcolor: '#006e52' } }}>
              Reply to Ticket
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/*  REPLY DIALOG - Shows conversation and reply box                       */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={openReplyDialog} onClose={handleCloseReplyDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', height: '82vh', display: 'flex', flexDirection: 'column' } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e1e3e5' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1, mr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.3 }}>
                  #{replyTicket?.id} — {replyTicket?.subject}
                </Typography>
                {replyTicket && <Chip label={STATUS_LABEL[replyTicket.status]} size="small"
                  sx={{ fontSize: '11px', fontWeight: 600, bgcolor: STATUS_CONFIG[replyTicket.status].bgcolor, color: STATUS_CONFIG[replyTicket.status].color }} />}
              </Box>
              <Typography variant="caption" sx={{ color: '#6d7175' }}>
                Customer: {replyTicket?.display_name} | Channel: {replyTicket && getChannelCfg(ticketChannel(replyTicket)).label}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseReplyDialog} size="small" sx={{ flexShrink: 0 }}><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f8f9fa' }}>
          {loadingReplyChat ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress size={28} sx={{ color: '#008060' }} /></Box>
          ) : replyReplies.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <SupportAgent sx={{ fontSize: 48, color: '#c9cccf', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">No conversation yet. Start the conversation by sending a reply.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {replyReplies.map((reply, idx) => {
                // First message (idx === 0) is from customer, then alternating
                const isCustomer = idx === 0 || idx % 2 === 0;
                return (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: isCustomer ? 'flex-start' : 'flex-end', mb: 1.5 }}>
                    <Box sx={{ 
                      maxWidth: '80%', 
                      bgcolor: isCustomer ? '#f5f5f5' : '#008060', 
                      border: isCustomer ? '1px solid #e0e0e0' : 'none',
                      borderRadius: isCustomer ? '4px 12px 12px 12px' : '12px 4px 12px 12px', 
                      p: 1.5 
                    }}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', fontSize: '10px', color: isCustomer ? 'text.secondary' : '#fff' }}>
                        {isCustomer ? (replyTicket?.display_name ?? 'Customer') : 'Support Team'}
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.5, wordBreak: 'break-word', color: isCustomer ? 'inherit' : '#fff' }}>
                        {reply.message}
                      </Typography>
                      {reply.created_at && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: isCustomer ? 'left' : 'right', fontSize: '10px', color: isCustomer ? 'text.secondary' : '#e0e0e0' }}>
                          {fmtDateTime(reply.created_at)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <div ref={bottomRef} />
            </Box>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ flexDirection: 'column', gap: 1.5, p: 2, alignItems: 'stretch' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 500, whiteSpace: 'nowrap' }}>Set status:</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select<string> value={String(replyStatus)}
                onChange={(e: SelectChangeEvent<string>) => setReplyStatus(Number(e.target.value) as TicketStatusNum)}
                sx={{ borderRadius: '8px', fontSize: '13px' }}>
                {([0, 1, 2, 3] as TicketStatusNum[]).map(s => (
                  <MenuItem key={s} value={String(s)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{STATUS_CONFIG[s].icon}<Typography variant="body2">{STATUS_LABEL[s]}</Typography></Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField 
              multiline 
              maxRows={4} 
              size="small" 
              placeholder="Type your reply… (Enter to send)" 
              fullWidth
              value={replyText} 
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
            />
            <Button 
              variant="contained" 
              onClick={handleSendReply} 
              disabled={!replyText.trim() || sendingReply}
              sx={{ bgcolor: '#008060', minWidth: 0, width: 44, height: 44, borderRadius: '12px', p: 0, flexShrink: 0, '&:hover': { bgcolor: '#006e52' }, '&:disabled': { bgcolor: '#c9cccf' } }}>
              {sendingReply ? <CircularProgress size={18} color="inherit" /> : <Send sx={{ fontSize: 18, color: 'white' }} />}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/*  CREATE TICKET DIALOG                                                 */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={openNewTicket} onClose={closeNew} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: '92vh' } }}>
        <DialogTitle sx={{ pb: 1.5, borderBottom: '1px solid #e1e3e5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#202223' }}>Create Support Ticket</Typography>
              <Typography variant="caption" sx={{ color: '#6d7175' }}>Raised by Admin</Typography>
            </Box>
            <IconButton size="small" onClick={closeNew}><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            <FormControl size="small" fullWidth required>
              <InputLabel>Channel *</InputLabel>
              <Select<TicketChannel> value={form.channel} label="Channel *"
                onChange={(e: SelectChangeEvent<TicketChannel>) => setForm(p => ({ ...p, channel: e.target.value as TicketChannel }))}
                sx={{ borderRadius: '8px' }}>
                {CHANNEL_KEYS.map(k => (
                  <MenuItem key={k} value={k}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {CHANNEL_CONFIG[k].icon}
                      <Typography variant="body2">{CHANNEL_CONFIG[k].label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth required>
              <InputLabel>Nature of Complaint *</InputLabel>
              <Select<string> value={form.nature_of_complaint} label="Nature of Complaint *"
                onChange={(e: SelectChangeEvent<string>) => setForm(p => ({ ...p, nature_of_complaint: e.target.value }))}
                sx={{ borderRadius: '8px' }}>
                {NATURE_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>

            <Divider><Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600 }}>Customer Info</Typography></Divider>

            <Autocomplete
              options={userOptions} loading={usersLoading} value={selectedUser}
              onChange={(_, v) => handleUserSelect(v)}
              getOptionLabel={(o) => `${o.full_name}${o.email ? ` — ${o.email}` : ''}${o.phone ? ` (${o.phone})` : ''}`}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              noOptionsText="No users found"
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.full_name || '—'}</Typography>
                    <Typography variant="caption" sx={{ color: '#6d7175' }}>{[option.email, option.phone].filter(Boolean).join(' · ')}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Select Customer *" placeholder="Search by name or email…"
                  error={!selectedUser && form.name === ''}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <><Person sx={{ fontSize: 18, color: '#6d7175', mr: 0.5, ml: 0.5 }} />{params.InputProps.startAdornment}</>,
                    endAdornment: <>{usersLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>,
                  }}
                  sx={fieldSx} />
              )}
            />

            {selectedUser && (
              <>
                <TextField size="small" label="Customer Name *" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: '#6d7175' }} /></InputAdornment> }}
                  sx={fieldSx} />
                <TextField size="small" label="Email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: '#6d7175' }} /></InputAdornment> }}
                  sx={fieldSx} />
                <TextField size="small" label="Phone Number" value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 18, color: '#6d7175' }} /></InputAdornment> }}
                  sx={fieldSx} />
              </>
            )}

            <Divider><Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600 }}>Order & Products</Typography></Divider>

            {!selectedUser ? (
              <Box sx={{ bgcolor: '#f9fafb', border: '1px dashed #c9cccf', borderRadius: '8px', p: 2, textAlign: 'center' }}>
                <ShoppingCart sx={{ fontSize: 28, color: '#c9cccf', mb: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '13px' }}>
                  Select a customer above to load their orders
                </Typography>
              </Box>
            ) : fetchingOrders ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#f9fafb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                <CircularProgress size={18} sx={{ color: '#008060' }} />
                <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '13px' }}>Loading orders…</Typography>
              </Box>
            ) : userOrders.length === 0 ? (
              <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#856404', fontSize: '13px' }}>No orders found for this customer</Typography>
              </Box>
            ) : (
              <FormControl size="small" fullWidth required error={!form.order_id}>
                <InputLabel>Select Order *</InputLabel>
                <Select<string>
                  value={form.order_id ? String(form.order_id) : ''}
                  label="Select Order *"
                  onChange={(e: SelectChangeEvent<string>) => {
                    const selectedValue = e.target.value;
                    if (selectedValue) {
                      handleOrderSelect(selectedValue);
                    }
                  }}
                  sx={{ borderRadius: '8px' }}
                  startAdornment={<InputAdornment position="start"><ShoppingCart sx={{ fontSize: 18, color: '#6d7175' }} /></InputAdornment>}
                >
                  {userOrders.map(o => (
                    <MenuItem key={o.order_id} value={String(o.order_id)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{o.order_id}</Typography>
                          {o.invoice_no && <Typography variant="caption" sx={{ color: '#6d7175' }}>({o.invoice_no})</Typography>}
                          <Chip label={orderStatusLabel(o.status)} size="small" sx={{ fontSize: '10px', height: 18, ml: 'auto' }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#6d7175' }}>
                          {fmtDate(o.order_date)} · ₹{Number(o.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {!form.order_id && selectedUser && userOrders.length > 0 && (
                  <FormHelperText error>Please select an order</FormHelperText>
                )}
              </FormControl>
            )}

            {selectedOrder && (
              fetchingProducts ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#f9fafb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                  <CircularProgress size={18} sx={{ color: '#008060' }} />
                  <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '13px' }}>Loading products…</Typography>
                </Box>
              ) : orderProducts.length === 0 ? (
                <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#856404', fontSize: '13px' }}>No products found in this order</Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Inventory2 sx={{ fontSize: 16, color: '#202223' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Select Products *</Typography>
                    </Box>
                    {selectedProducts.length > 0 && <Chip label={`${selectedProducts.length} selected`} size="small" />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button size="small" onClick={() => { const all = orderProducts.map(p => p.product_id); setSelectedProducts(all); setForm(f => ({ ...f, product_ids: all })); }}>
                      Select all
                    </Button>
                    <Button size="small" onClick={() => { setSelectedProducts([]); setForm(f => ({ ...f, product_ids: [] })); }}>
                      Clear
                    </Button>
                  </Box>
                  <Box sx={{ border: '1px solid #e1e3e5', borderRadius: '10px', maxHeight: 240, overflowY: 'auto' }}>
                    <List dense disablePadding>
                      {orderProducts.map((product, idx) => {
                        const checked = selectedProducts.includes(product.product_id);
                        return (
                          <React.Fragment key={product.product_id}>
                            {idx > 0 && <Divider />}
                            <ListItemButton onClick={() => toggleProduct(product.product_id)} sx={{ py: 1 }}>
                              <Checkbox checked={checked} size="small" sx={{ mr: 1 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 400 }}>{product.product_name}</Typography>
                                <Typography variant="caption" color="text.secondary">Qty: {product.quantity} | ₹{Number(product.unit_price).toLocaleString('en-IN')}</Typography>
                              </Box>
                            </ListItemButton>
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </Box>
                </Box>
              )
            )}

            <Divider><Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600 }}>Ticket Details</Typography></Divider>

            <TextField size="small" label="Subject *" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
            <TextField size="small" label="Description" multiline rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />

            <FormControl size="small" fullWidth>
              <InputLabel>Initial Status</InputLabel>
              <Select<string> value={String(form.status)} label="Initial Status"
                onChange={(e: SelectChangeEvent<string>) => setForm(p => ({ ...p, status: Number(e.target.value) as TicketStatusNum }))}>
                {([0, 1, 2, 3] as TicketStatusNum[]).map(s => (
                  <MenuItem key={s} value={String(s)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{STATUS_CONFIG[s].icon}{STATUS_LABEL[s]}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={closeNew} sx={{ bgcolor: '#f44336', color: '#fff', border: 'none', '&:hover': { bgcolor: '#d32f2f' } }}>Close</Button>
          <Button variant="contained" onClick={handleCreateTicket} disabled={creatingTicket || !form.name.trim() || !form.order_id || form.product_ids.length === 0 || !form.nature_of_complaint || !form.subject.trim()}
            sx={{ bgcolor: '#008060', '&:hover': { bgcolor: '#006e52' } }}>
            {creatingTicket ? <CircularProgress size={18} /> : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Ticket</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this ticket? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deletingTicket}>
            {deletingTicket ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportManagement;